import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_DOCUMENT_TEMPLATES } from '../../contracts';
import { getDocumentTemplates } from '../api/templates';
import { getTemplatesDataConfig } from '../config';
import { sortTemplates } from '../sortTemplates';

/**
 * Module-scope invalidator — runs outside a hook, so it takes the app's
 * queryClient singleton from the `configureTemplatesData` seam.
 */
export const invalidateDocumentTemplates = () =>
  getTemplatesDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_DOCUMENT_TEMPLATES],
  });

type DocumentTemplates = Awaited<
  ReturnType<typeof getDocumentTemplates>
>['data']['templates'];

function getCachedDocumentTemplates(): DocumentTemplates | undefined {
  return getTemplatesDataConfig().queryClient.getQueryData<DocumentTemplates>([
    GET_DOCUMENT_TEMPLATES,
  ]);
}

/**
 * The document-template list (D2.7, ARCFE-1046).
 *
 * `isAuthenticated` is the app's `state.auth.token` read and `enabled` its
 * `useIsDesktopOnlineReady()` read — both composed app-side (cohort-C), and
 * both kept as separate booleans because the original `enabled` was their
 * conjunction and collapsing them would lose which one gated a given render.
 *
 * TWO sorts, chained, and the order matters: the `created_time` comparator runs
 * first and `sortTemplates` second, so team/favourite precedence wins over
 * recency. Moved as-is.
 */
export function useQueryDocumentTemplates(
  isAuthenticated: boolean,
  isDesktopOnlineReady: boolean,
  onRequestError: (error: AxiosError) => void
) {
  return useQuery({
    enabled: isAuthenticated && isDesktopOnlineReady,
    queryKey: [GET_DOCUMENT_TEMPLATES],
    queryFn: async () => {
      try {
        const { data } = await getDocumentTemplates();
        return data.templates
          .sort((a, b) => {
            if (!a.created_time) return 1;
            if (!b.created_time) return -1;
            if (a.created_time && b.created_time) {
              return a.created_time < b.created_time ? 1 : -1;
            }
            return 0;
          })
          .sort(sortTemplates);
      } catch (error) {
        if (error instanceof AxiosError) {
          if (getTemplatesDataConfig().isOfflineNetworkError(error)) {
            const cached = getCachedDocumentTemplates();
            if (cached) {
              return cached;
            }
            // No cache to fall back on — rethrow so the query enters an error
            // state and refetches on reconnect, rather than caching an empty
            // success that the global staleTime would treat as fresh.
            throw error;
          }

          onRequestError(error);
        }
      }
    },
  });
}
